import React, { FC, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { styled } from "@mui/system";
import { GAPS, SPACINGS } from "../../common/consts";
import Card from "../../components/Display/Card";
import Text, { TextVariant } from "../../components/Display/Text";
import Button, { ButtonSizes, ButtonVariants } from "../../components/Display/Button";
import Checkbox from "../../components/DataInput/Checkbox";
import Loading from "../../components/Display/Loading";
import { decideMcpAuthorization, getMcpAuthorizationRequest } from "../../services/firebase";
import { McpAuthorizationRequest } from "../../types/mcp";
import { POST_LOGIN_PATH_KEY } from "../../services/extension";

const Page = styled("div")`
  display: flex;
  justify-content: center;
  padding: ${SPACINGS.xl};
`;

const Content = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${GAPS.md};
  max-width: 560px;
  width: 100%;
`;

const List = styled("ul")`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: ${GAPS.sm};
`;

const Row = styled("label")`
  display: flex;
  gap: ${GAPS.sm};
  align-items: flex-start;
  cursor: pointer;
`;

const Actions = styled("div")`
  display: flex;
  gap: ${GAPS.sm};
  justify-content: flex-end;
`;

const Muted = styled(Text)`
  opacity: 0.7;
`;

const CRM_NAMES: Record<string, string> = { hubspot: "HubSpot", outreach: "Outreach" };

/**
 * Where an AI app (Claude, ChatGPT) sends the user to connect boring: they
 * are signed in with their boring login already (signed-out visitors come
 * back here after signing in), see which app asks and where it sends them
 * back, choose whether it may make changes, and approve or decline.
 */
const McpAuthorize: FC = () => {
  const [params] = useSearchParams();
  const requestId = params.get("request") || "";
  const [request, setRequest] = useState<McpAuthorizationRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allowWrite, setAllowWrite] = useState(true);
  const [deciding, setDeciding] = useState<"approve" | "decline" | null>(null);

  useEffect(() => {
    // Back from signing in, or here directly: either way this visit is the one it was kept for.
    if (sessionStorage.getItem(POST_LOGIN_PATH_KEY)?.startsWith("/mcp/authorize")) sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
    if (!requestId) {
      setError("This link is missing its sign-in request. Start connecting again from your AI app.");
      return;
    }
    getMcpAuthorizationRequest(requestId)
      .then(setRequest)
      .catch((e) => setError(e?.message || "This sign-in link has expired. Start connecting again from your AI app."));
  }, [requestId]);

  const decide = async (approve: boolean) => {
    if (!request) return;
    setDeciding(approve ? "approve" : "decline");
    try {
      const redirectUrl = await decideMcpAuthorization(request.requestId, approve, approve && request.canWrite && allowWrite);
      window.location.assign(redirectUrl);
    } catch (e: any) {
      setError(e?.message || "Could not finish connecting. Start again from your AI app.");
      setDeciding(null);
    }
  };

  if (error) {
    return (
      <Page>
        <Content>
          <Text variant={TextVariant.h4}>Connect an AI app</Text>
          <Text variant={TextVariant.sm}>{error}</Text>
        </Content>
      </Page>
    );
  }
  if (!request) return <Loading />;

  const crm = CRM_NAMES[request.organization?.crm || ""] || "your CRM";
  return (
    <Page>
      <Content>
        <Text variant={TextVariant.h4}>Connect {request.clientName} to boring</Text>
        <Muted variant={TextVariant.sm}>
          Signed in as {request.user.email}
          {request.organization ? ` in ${request.organization.name || "your organization"}` : ""}. After you decide, you go back to {request.redirectHost}.
        </Muted>
        {!request.organization ? (
          <Text variant={TextVariant.sm}>Your boring account is not part of an organization yet, so there is nothing to connect.</Text>
        ) : (
          <>
            <Text variant={TextVariant.md}>{request.clientName} will be able to:</Text>
            <List>
              <li>
                <Text variant={TextVariant.sm}>Read your boring reports, settings, users, billing and LinkedIn inbox, as you see them in the dashboard.</Text>
              </li>
              <li>
                <Text variant={TextVariant.sm}>Search and read {crm} records through boring&apos;s connection (read only; it never changes them).</Text>
              </li>
            </List>
            {request.canWrite && (
              <Row>
                <Checkbox checked={allowWrite} onChange={setAllowWrite} aria-label="Allow changes" />
                <Text variant={TextVariant.sm}>
                  Also allow changes: settings, users and licenses{request.user.isAdmin ? ", billing" : ""}, and LinkedIn actions from your own account. Anything that removes access, changes billing,
                  or messages or posts on LinkedIn asks you to confirm in the chat first.
                </Text>
              </Row>
            )}
            <Muted variant={TextVariant.sm}>
              The app&apos;s name is how it described itself. Only connect apps you trust; you can disconnect them any time in Settings, Connected AI apps.
            </Muted>
          </>
        )}
        <Actions>
          <Button variant={ButtonVariants.light} size={ButtonSizes.md} onClick={() => decide(false)} loading={deciding === "decline"} disabled={!!deciding}>
            Decline
          </Button>
          {request.organization && (
            <Button variant={ButtonVariants.brand} size={ButtonSizes.md} onClick={() => decide(true)} loading={deciding === "approve"} disabled={!!deciding}>
              Connect
            </Button>
          )}
        </Actions>
      </Content>
    </Page>
  );
};

export default McpAuthorize;
